import {
  PageUnhandledErrorAction,
  PageLoadAction,
  PageLoadCachedAction,
  PageLoadSuccessAction,
  PageUpdateAction,
  PageUpdateSuccessAction,
  PageUpdateFailureAction,
  PageLoadFailureAction,
  PAGE_UNHANDLED_ERROR,
  PAGE_LOAD,
  PAGE_LOAD_CACHED,
  PAGE_LOAD_SUCCESS,
  PAGE_LOAD_DONE,
  PAGE_LOAD_ABORTED,
  PAGE_UPDATE,
  PAGE_UPDATE_SUCCESS,
  PAGE_UPDATE_FAILURE,
  PAGE_LOAD_FAILURE,
} from './action-creators';
import URLX, { relativeUrl } from '../Url';
import { Page } from '../State';
import { Action } from 'redux';
import * as cache from './cache';
import { appShellComponentName } from '../AppShell/utils';

export default function(state: Page, action: Action, appShellData: Scope.AppShellDataBase): Page {
  // `isLoading` is not set when we get the state from the dehydration
  // so we use that to determine if this is the initial state
  const isInitial = state.isLoading === undefined;

  // `appShellComponentName` is only set in Service Worker context and `null` otherwise
  const isAppShell = state.componentName === appShellComponentName;

  if (isInitial && !isAppShell) {
    // Set the cache here as we won't get PAGE_LOAD_SUCCESS after a hard page load
    cache.setCacheItem(state as any);
  }

  if (isInitial) {
    state = Object.assign({}, state, {
      isLoading: false,
      isPartial: false,
      loadFailure: null,
      unhandledError: null,
      scrollPosition: undefined,
      url: state.url,
      isServerSideRendered: window.IS_SERVER_SIDE_RENDERED,
    });
  }

  let pageToCache: any = null;
  let fastCacheOnly = false;

  switch (action.type) {
    case PAGE_LOAD: {
      const pageLoadAction = action as PageLoadAction;

      // The page load action can be dispatched without being the current page. The page load
      // action is dispatched after we look for the page in the cache and the cache can take some
      // time to respond. If the user clicks on another link during the time that we're looking
      // in the cache the page load action is not considered to be for the current page.
      if (pageLoadAction.isCurrentPage) {
        state = {
          ...state,
          ...{
            scrollPosition: undefined,
            isLoading: true,
            loadFailure: null,
            unhandledError: state.unhandledError,
            isServerSideRendered: false,
          },
        };
      } else {
        console.debug('Ignoring page load response because another request is in-flight.');
      }
      break;
    }
    case PAGE_LOAD_CACHED: {
      const pageCacheAction = action as PageLoadCachedAction;
      const { cachedItem } = pageCacheAction;

      if (cachedItem) {
        // The page load action can be dispatched without being the current page. The page load
        // action is dispatched after we look for the page in the cache and the cache can take some
        // time to respond. If the user clicks on another link during the time that we're looking
        // in the cache the page load action is not considered to be for the current page.
        if (pageCacheAction.isCurrentPage) {
          state = Object.assign({}, cachedItem, {
            componentName: cachedItem.componentName,
            url: relativeUrl(pageCacheAction.url),
            isPartial: pageCacheAction.isPartial,
            isLoading: true,
            loadFailure: null,
            unhandledError: state.unhandledError,
            scrollPosition: getScrollPosition(state, pageCacheAction),
          } as Page);
        } else {
          console.debug('Ignoring cached page load because another request is in-flight.');
        }
      } else {
        console.debug('Ignoring cached page load because cache was missing.');
      }
      break;
    }
    case PAGE_UPDATE: {
      const pageUpdateAction = action as PageUpdateAction;
      const currentPageUpdates = state.pageUpdateRequestIds || [];
      if (
        !state.isLoading ||
        (pageUpdateAction.requestId && currentPageUpdates.indexOf(pageUpdateAction.requestId) === -1)
      ) {
        let pageUpdateRequestIds: string[] = [];
        if (pageUpdateAction.requestId) {
          pageUpdateRequestIds = [...currentPageUpdates, pageUpdateAction.requestId];
        }

        state = Object.assign({}, state, {
          isLoading: true,
          pageUpdateRequestIds,
        } as Page);
      }
      break;
    }
    case PAGE_UPDATE_FAILURE: {
      let pageUpdateRequestIds = state.pageUpdateRequestIds || [];
      const pageUpdateFailureAction = action as PageUpdateFailureAction;
      const failureRequestIds = requestIds(pageUpdateFailureAction.requestIds);
      if (failureRequestIds.length) {
        failureRequestIds.forEach(requestId => {
          if (pageUpdateRequestIds.indexOf(requestId) === -1) {
            throw new Error(
              'Recieved PAGE_UPDATE_FAILURE action for request ' +
                requestId +
                ' which has not previously been passed to a PAGE_UPDATE action',
            );
          }
        });
        pageUpdateRequestIds = pageUpdateRequestIds.filter(id => failureRequestIds.indexOf(id) === -1);
        const isLoading = pageUpdateRequestIds.length > 0;
        state = Object.assign({}, state, {
          isLoading,
          pageUpdateRequestIds,
        } as Page);
      } else if (state.isLoading && pageUpdateRequestIds.length === 0) {
        state = Object.assign({}, state, {
          isLoading: false,
        } as Page);
      }
      break;
    }
    case PAGE_UNHANDLED_ERROR: {
      state = Object.assign({}, state, {
        unhandledError: (action as PageUnhandledErrorAction).error,
      } as Page);
      break;
    }
    case PAGE_LOAD_SUCCESS: {
      const pageLoadSuccessAction = action as PageLoadSuccessAction;
      const options = pageLoadSuccessAction.options;

      const merge = options && typeof options.merge === 'function' && options.merge;
      let shouldCache = true;
      const page = pageLoadSuccessAction.page;

      let newPage: any;
      // We don't want to merge if it's not the current page because the merger
      // will try to merge the previous page with the new one, and that can be
      // a completely different page type
      if (merge) {
        if (pageLoadSuccessAction.isCurrentPage) {
          newPage = merge(state, page);
        } else {
          // If the page is supposed to be merged but can't because it's not the
          // current page we can't cache it
          shouldCache = false;
          newPage = {};
        }
      } else {
        newPage = page;
      }
      const url = pageLoadSuccessAction.url;

      // `newPage.url` is what the server thinks is the url, but that can contain
      // the hidden query strings. Instead we overwrite that with what we requested
      // which is what we use as cache key.
      newPage.url = relativeUrl(url);

      if (shouldCache) {
        pageToCache = newPage;
      }

      // We don't want to cache merged pages because that eats up the cache with duplicated
      // content
      if (merge) {
        fastCacheOnly = true;
        console.debug('Only caching', newPage.url, 'in fast cache because it has been merged');
      }

      // We might get a load success action for a page that is no longer
      // the current page. Like if the user requested a page but got tired of waiting
      // for it and went to another page instead.
      if (pageLoadSuccessAction.isCurrentPage) {
        state = Object.assign({}, newPage, {
          isPartial: false,
          loadFailure: null,
          unhandledError: state.unhandledError,
          scrollPosition: getScrollPosition(state, pageLoadSuccessAction),
          isLoading: state.isLoading,
          isServerSideRendered: false,
        } as Page);
      } else {
        if (shouldCache) {
          console.debug(
            'Ignoring page load response because another request is in-flight. The full response has been cached.',
          );
        } else {
          console.debug(
            'Ignoring page load response because another request is in-flight. The full response has not been cached.',
          );
        }
      }
      break;
    }
    case PAGE_UPDATE_SUCCESS: {
      const pageUpdateSuccessAction = action as PageUpdateSuccessAction;
      const successRequestIds = requestIds(pageUpdateSuccessAction.requestIds);
      let pageUpdateRequestIds = state.pageUpdateRequestIds || [];
      if (successRequestIds.length) {
        successRequestIds.forEach(requestId => {
          if (pageUpdateRequestIds.indexOf(requestId) === -1) {
            throw new Error(
              'Recieved PAGE_UPDATE_SUCCESS action for request ' +
                requestId +
                ' which has not previously been passed to a PAGE_UPDATE action',
            );
          }
        });
        pageUpdateRequestIds = pageUpdateRequestIds.filter(id => successRequestIds.indexOf(id) === -1);
      }
      pageToCache = pageUpdateSuccessAction.page;
      state = Object.assign({}, state, pageUpdateSuccessAction.page, {
        scrollPosition: undefined,
        pageUpdateRequestIds,
        isLoading: pageUpdateRequestIds.length > 0,
      } as Page);
      break;
    }
    case PAGE_LOAD_FAILURE: {
      state = Object.assign({}, state, {
        loadFailure: (action as PageLoadFailureAction).loadFailure,
        reload: (action as PageLoadFailureAction).retry,
        unhandledError: state.unhandledError,
      } as Page);
      break;
    }
    case PAGE_LOAD_ABORTED:
    case PAGE_LOAD_DONE: {
      if (state.isLoading) {
        state = Object.assign({}, state, { isLoading: false } as Page);
      }
      break;
    }
    default:
      break;
  }

  if (pageToCache) {
    cache.setCacheItem(pageToCache, fastCacheOnly);
  }
  return state;
}

function getScrollPosition(previousState: Page, action: PageLoadCachedAction | PageLoadSuccessAction) {
  if (new URLX(previousState.url).pathname !== action.url.pathname) {
    // We only want to change scroll position if the path has changed, not if
    // only the query string has changed
    return action.scrollPosition;
  }
  return undefined;
}

function requestIds(ids: string | string[] | null) {
  if (!ids) {
    return [];
  }
  return Array.isArray(ids) ? ids : [ids];
}
